import React from 'react';
import { useTranslation } from 'react-i18next';
import { TitleValue, Tooltip } from '@kaleyra/emmy';
import { IColumn } from '@kaleyra/emmy/dist/molecules/styledTable';
import moment from 'moment';

import InfoAlert from '../../../../../assets/InfoAlert.svg';

import {
	IDeliveryStatus,
	ChannelType,
	DeliveryStatusFilterType,
	DefaultPropertyType,
} from '../../../../../interface';
import { Instances, CallbackType } from '../../../../../constants';
import SeeMoreModal, {
	BaseSeeMorePropsType,
} from '../../../templates/SeeMoreModal';
import TableFactory from '../../../templates/TableTemplate';
import TitleSearchTable from '../../../templates/TitleSearchTable';

import Header from '../Header';

import Styles from './index.module.scss';
import { debounce, isEmpty } from 'lodash';
import infoIcon from '../../../../../assets/InfoIconGray.svg'
import { checkIndianAccount } from '../../../../../utils';

export type MapStateType = {
	deliveryStatus: IDeliveryStatus[];
	inProgress: boolean,
	filters: DeliveryStatusFilterType;
	inProgressTabular: boolean;
};

export type DispatchPropsType = {
	onFilter: (filters: DeliveryStatusFilterType) => void;
	onSearchChange: (searchStr?: string) => void;
	onLoad: (filters: DeliveryStatusFilterType) => void;
};

type PropsType = MapStateType & BaseSeeMorePropsType & DispatchPropsType;

const DeliveryStatusTable = TableFactory(Instances.deliveryStatusDetailed);

type RenderBodyPropsType = {
	currentTab: string;
}

const TabBody: React.FC<RenderBodyPropsType & MapStateType & DispatchPropsType> = ({
	deliveryStatus,
	filters,
	onFilter,
	onSearchChange,
	onLoad,
	inProgress,
	inProgressTabular,
	currentTab,

}) => {


	const { t } = useTranslation();
	const getInfo = (contentValue: string) => {
		return (
			<Tooltip
				title={contentValue}
				placement="bottom"
				color="black"
				key="white"
				children={<img src={infoIcon} alt="alert" />}
			/>
		);
	};


	const getColumn = (currentTabVal: string): Array<IColumn> => {
		switch (currentTabVal as ChannelType) {
			case ChannelType.sms:
				return [
					{
						header: t('deliveryStatusWidgetSeeMoreStatusCode'),
						accessor: 'trace',
					},
					{
						header: t('deliveryStatusWidgetTableStatus'),
						accessor: 'code',
						alignHeader: 'left',
						Cell: ({ value, original }) => (
							<div className={Styles.statusCol}>
								<p>{value}</p>
								{getInfo(original?.description)}
							</div>
						),
					},
					{
						header: 'Status',
						accessor: 'status',
						alignHeader: 'left',
						hide: true,

						Cell: ({ value, original }) => (
							<div className={Styles.statusCol}>
								<p>{value}</p>
								<div>
									{['Not Sent', 'Sent'].includes(value) ?
										<Tooltip
											title={value === 'Not Sent' ? t("KLR02595") : t("KLR02589")}
											placement="bottom"
											color="black"
											key="white"
											children={<img src={infoIcon} alt="alert" className={Styles.statusInfoIcon} />}
										/>
										: null}
								</div>
							</div>
						),
					},
					{
						header: t('deliveryStatusWidgetTablePercentage'),
						accessor: 'percentage',
						alignHeader: 'right',
						Cell: ({ value }) => `${value}%`,
					},
					{
						header: t('mainTotal'),
						accessor: 'count',
						alignHeader: 'right',
					},
				];
			case ChannelType.whatsApp:
				return [
					{
						header: t('deliveryStatusWidgetTableStatus'),
						accessor: 'label',
						alignHeader: 'left',
					},
					{
						header: t('deliveryStatusWidgetTablePercentage'),
						accessor: 'percentage',
						alignHeader: 'right',
						Cell: ({ value }) => `${value}%`,
					},
					{
						header: t('mainTotal'),
						accessor: 'count',
						alignHeader: 'right',
					},
				];
			default:
				return [];
		}
	};
	const getHeaderData = () => {
		const { overall: overallVal } = deliveryStatus[0];
		switch (filters.channel) {
			case ChannelType.sms:
				return [
					{
						label: t("KLR02600"),
						value: overallVal?.delivered_rate ? `${overallVal?.delivered_rate}%` : "0%",
					},
					{
						label:  t("KLR02585"),
						value: overallVal?.sent ? overallVal?.sent : "",
					},
					{
						label: t('performanceWidgetTTlDelivered'),
						value: overallVal?.delivered ? overallVal?.delivered : "",
					}
				];
			case ChannelType.whatsApp:
				return [
					{
						label: `${t('performanceWidgetTTlDeliveredPercentage')}`,
						value: overallVal?.delivered ? `${overallVal?.delivered}%` : "",
					},
					{
						label: t('deliveryStatusWidgetSent'),
						value: overallVal?.sent ? overallVal?.sent : "",
					},
					{
						label: t('deliveryStatusWidgetRead'),
						value: overallVal?.read ? `${overallVal?.read}%` : "",
					},
				];
			default:
				return [];
		}
	};

	const {
		overall,
		// status = [] 
	} = deliveryStatus[0];
	const { timerange } = filters;
	const { start_time: start, end_time: end } = timerange || {};
	const headers = getHeaderData();

	const onSearch = (searchStr: string = '') => {
		onSearchChange(searchStr);
	};

	const debouncedChange = React.useCallback<CallbackType>(
		debounce((value: any = "") => onSearch(value), 400),
		[currentTab]
	);

	return (
		<>
			<div className={Styles.tableContainer}>
				{!inProgress && !inProgressTabular && !isEmpty(overall) ? (
					<div className={Styles.titleContainer}>
						{headers.map((item) => (
							<TitleValue
								label={item.label}
								labelPosition={'top'}
							>
								<h2>{item.value || 0}</h2>
							</TitleValue>
						))}
					</div>
				) : null}
				<TitleSearchTable
					title={`${t(
						`channelType${currentTab}`
					)} Delivery Status - ${moment.unix(+start).format(
						'DD MMM YYYY'
					)} to ${moment.unix(+end).format('DD MMM YYYY')}`}
					onChange={debouncedChange}
					currentTab={currentTab}
				>
					<DeliveryStatusTable
						columns={getColumn(currentTab)}
						filters={filters}
						property={'status'}
						searchTerm={'status'}
					/>
				</TitleSearchTable>
			</div>
		</>
	);
};

const DeliveryStatusSeeMore: React.FC<PropsType> = ({
	deliveryStatus,
	filters,
	onFilter,
	onSearchChange,
	onLoad,
	inProgress,
	inProgressTabular,
	...props
}) => {


	const onTabClick = (channel: string) => {
		onLoad({
			...filters,
			channel: channel as ChannelType,
		});
	};

	const customChannelOptions = (): Array<DefaultPropertyType> => {
		let channels: Array<DefaultPropertyType> = [];
		channels.push({
			label: 'SMS',
			value: 'sms'
		})
		// if (deliveryStatus && deliveryStatus[0].channel_status.whatsapp) {
		// 	channels.push({
		// 		label: 'WhatsApp',
		// 		value: 'whatsapp'
		// 	})
		// }
		return channels;
	}

	return (
		<SeeMoreModal
			header={
				<Header
					filters={filters}
					onFilterChange={onFilter}
					showDropDownButton
					showChannelFilter={false}
				/>
			}
			getBody={(currentTab) => <TabBody currentTab={currentTab} onSearchChange={onSearchChange} deliveryStatus={deliveryStatus} onFilter={onFilter} inProgress={inProgress} inProgressTabular={inProgressTabular} filters={filters} onLoad={onLoad} />}
			tabs={customChannelOptions()}
			parentTab={filters.channel}
			onTabClick={onTabClick}
			{...props}
		/>
	);
};

export default DeliveryStatusSeeMore;
